import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";
import { Label } from "@workspace/amarmeds-ds/components/ui/label";

import { api, ApiError, type OpsPharmacy, type OpsStaff } from "../api";
import { parseTaka, taka } from "../format";

const ROLE_LABEL: Record<OpsStaff["role"], string> = { pharmacy: "Pharmacy", rider: "Rider", ops: "Ops" };

export default function OpsStaff() {
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ["ops-staff"], queryFn: () => api<{ staff: OpsStaff[] }>("/ops/staff") });
  const pharmacies = useQuery({ queryKey: ["ops-pharmacies"], queryFn: () => api<{ pharmacies: OpsPharmacy[] }>("/ops/pharmacies") });

  const [name, setName] = useState("");
  const [role, setRole] = useState<OpsStaff["role"]>("pharmacy");
  const [pharmacyId, setPharmacyId] = useState("");
  const [phone, setPhone] = useState("");
  const [cashLimit, setCashLimit] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ name: string; token: string } | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api<{ staff: OpsStaff; token: string }>("/ops/staff", {
        body: { name, role, pharmacyId: role === "pharmacy" ? Number(pharmacyId) : null, phone, cashLimitPaisa: parseTaka(cashLimit) ?? undefined },
      }),
    onSuccess: (res) => {
      setCreated({ name: res.staff.name, token: res.token });
      setName("");
      setPhone("");
      setCashLimit("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["ops-staff"] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.code.replace(/_/g, " ") : "Could not add the staff member."),
  });

  const ready = name.trim() !== "" && (role !== "pharmacy" || pharmacyId !== "");

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Staff</h1>

      {created && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base">Access token for {created.name} — shown once</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="break-all font-mono text-sm">{created.token}</p>
            <p className="text-sm text-muted-foreground">Send this to them privately. It cannot be shown again.</p>
            <Button size="sm" variant="outline" onClick={() => setCreated(null)}>
              Done
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add staff</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="s-name">Name</Label>
              <Input id="s-name" placeholder="Counter A" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="s-role">Role</Label>
              <select id="s-role" className="min-h-9 w-full rounded-md border bg-background px-2" value={role} onChange={(e) => setRole(e.target.value as OpsStaff["role"])}>
                <option value="pharmacy">Pharmacy</option>
                <option value="rider">Rider</option>
                <option value="ops">Ops</option>
              </select>
            </div>
            {role === "pharmacy" && (
              <div className="space-y-1">
                <Label htmlFor="s-pharmacy">Pharmacy</Label>
                <select id="s-pharmacy" className="min-h-9 w-full rounded-md border bg-background px-2" value={pharmacyId} onChange={(e) => setPharmacyId(e.target.value)}>
                  <option value="">Choose a pharmacy</option>
                  {pharmacies.data?.pharmacies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (#{p.id})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="s-phone">Phone</Label>
              <Input id="s-phone" inputMode="tel" placeholder="01XXXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            {role === "rider" && (
              <div className="space-y-1">
                <Label htmlFor="s-limit">Cash limit (৳)</Label>
                <Input id="s-limit" inputMode="decimal" placeholder="5000" value={cashLimit} onChange={(e) => setCashLimit(e.target.value)} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button disabled={!ready || add.isPending} onClick={() => add.mutate()}>
              Add staff
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Staff</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {staff.data?.staff.map((s) => (
            <StaffRow key={s.id} staff={s} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

const STAFF_ERRORS: Record<string, string> = {
  invalid_name: "Give a name (up to 120 characters).",
  invalid_phone: "That is not a Bangladeshi mobile number. Leave it empty for none.",
  cannot_deactivate_self: "You cannot switch yourself off.",
};

function StaffRow({ staff }: { staff: OpsStaff }) {
  const qc = useQueryClient();
  const [limit, setLimit] = useState(String(staff.cashLimitPaisa / 100));
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(staff.name);
  const [phone, setPhone] = useState(staff.phone);
  const [error, setError] = useState<string | null>(null);
  const onError = (err: unknown) => setError(err instanceof ApiError ? (STAFF_ERRORS[err.code] ?? err.code.replace(/_/g, " ")) : "Could not save.");
  const toggle = useMutation({
    mutationFn: () => api(`/ops/staff/${staff.id}`, { method: "PATCH", body: { active: !staff.active } }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["ops-staff"] });
    },
    onError,
  });
  const saveDetails = useMutation({
    mutationFn: () => api(`/ops/staff/${staff.id}`, { method: "PATCH", body: { name, phone } }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["ops-staff"] });
    },
    onError,
  });
  if (editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 border-b pb-3 text-sm last:border-0">
        <Input className="w-48" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input className="w-40" aria-label="Phone" inputMode="tel" placeholder="01XXXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Button size="sm" disabled={!name.trim() || saveDetails.isPending} onClick={() => saveDetails.mutate()}>
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        {error && <p className="w-full text-xs text-destructive">{error}</p>}
      </div>
    );
  }
  const setLimitM = useMutation({
    mutationFn: () => api(`/ops/staff/${staff.id}`, { method: "PATCH", body: { cashLimitPaisa: parseTaka(limit) } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ops-staff"] }),
    onError,
  });
  const isRider = staff.role === "rider";
  return (
    <div className="flex flex-wrap items-center gap-3 border-b pb-3 text-sm last:border-0">
      <div className="min-w-40 flex-1">
        <div className="font-medium">{staff.name}</div>
        <div className="text-muted-foreground">
          {staff.phone || "no phone"}
          {staff.pharmacyId !== null ? ` · pharmacy ${staff.pharmacyId}` : ""}
        </div>
      </div>
      <Badge variant="secondary">{ROLE_LABEL[staff.role]}</Badge>
      <Badge variant={staff.active ? "outline" : "destructive"}>{staff.active ? "Active" : "Off"}</Badge>
      {isRider && (
        <div className="flex items-center gap-1">
          <Input className="w-24" inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} />
          <Button size="sm" variant="outline" disabled={parseTaka(limit) === null || setLimitM.isPending} onClick={() => setLimitM.mutate()}>
            Set limit
          </Button>
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setName(staff.name);
          setPhone(staff.phone);
          setError(null);
          setEditing(true);
        }}
      >
        Edit
      </Button>
      <Button size="sm" variant="outline" disabled={toggle.isPending} onClick={() => toggle.mutate()}>
        {staff.active ? "Deactivate" : "Activate"}
      </Button>
      {error && <p className="w-full text-xs text-destructive">{error}</p>}
    </div>
  );
}
